import { RefObject, useEffect } from 'react';

/**
 * Closes an open menu when attention moves away from it.
 *
 * Shared by the header's menus so they behave identically and the
 * awkward parts are solved once.
 */
export const useDismissableMenu = (
  open: boolean,
  close: () => void,
  containerRef: RefObject<HTMLElement | null>,
) => {
  useEffect(() => {
    if (!open) {
      return;
    }
    // Event rather than MouseEvent: the same handler is used for
    // touchstart, which delivers a TouchEvent.
    const onDocumentClick = (event: Event) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    // Listened for on the document so clicking anywhere else closes
    // it, which is what every other menu on the web does. touchstart
    // as well as mousedown: a tap does eventually produce a mouse
    // event, but only after the browser has decided it was not a
    // scroll or a double-tap, which leaves the menu sitting open for
    // a noticeable moment on a phone.
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('touchstart', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('touchstart', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
};
